<script setup lang="ts">
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  Brain,
  Network,
  Shield,
  Zap,
  LineChart,
  Settings,
} from "lucide-vue-next";

interface BenefitsProps {
  icon: string;
  title: string;
  description: string;
}

const benefitList: BenefitsProps[] = [
  {
    icon: "brain",
    title: "AI智能分析",
    description:
      "运用深度学习算法，实时分析设备运行数据，提前预警异常状况，大幅降低设备故障率和维护成本。",
  },
  {
    icon: "network",
    title: "物联网互联",
    description:
      "构建全方位物联网生态，实现设备间无缝连接与数据共享，打造智能化工业管理体系。",
  },
  {
    icon: "lineChart",
    title: "数据可视化",
    description:
      "专业的数据分析平台，将复杂的工业数据转化为直观图表，助力管理层快速决策。",
  },
  {
    icon: "shield",
    title: "安全可靠",
    description:
      "企业级安全防护体系，多重加密保障数据安全，符合国家信息安全标准，让您安心使用。",
  },
];

const iconMap: Record<
  string,
  | typeof Brain
  | typeof Network
  | typeof Shield
  | typeof Zap
  | typeof LineChart
  | typeof Settings
> = {
  brain: Brain,
  network: Network,
  shield: Shield,
  zap: Zap,
  lineChart: LineChart,
  settings: Settings,
};
</script>

<template>
  <section id="benefits" class="benefits-section">
    <div class="benefits-container">
      <div class="benefits-content">
        <h2 class="benefits-subtitle">智能化核心优势</h2>
        <h2 class="benefits-title">
          物联网+AI技术创新引领
          <br class="hidden md:block">
          工业自动化智能升级
        </h2>
        <p class="benefits-description">
          凭借先进的人工智能算法和物联网传感技术，我们为企业提供智能仪器仪表、工业自动化控制等全方位的智能化解决方案，
          助力传统产业实现数字化转型，提升运营效率和竞争优势。
        </p>
      </div>

      <div class="benefits-grid">
        <Card v-for="({ icon, title, description }, index) in benefitList" :key="title"
          class="benefit-card group/number">
          <CardHeader>
            <div class="benefit-header">
              <component class="benefit-icon" :is="iconMap[icon]" />
              <span class="benefit-number">0{{ index + 1 }}</span>
            </div>
            <CardTitle>{{ title }}</CardTitle>
          </CardHeader>
          <CardContent class="benefit-description">{{ description }}</CardContent>
        </Card>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Benefits 主要样式 */
.benefits-section {
  @apply container py-24 sm:py-32;
}

.benefits-container {
  @apply grid lg:grid-cols-2 place-items-center lg:gap-24;
}

.benefits-content {
  @apply w-full;
}

.benefits-subtitle {
  @apply text-lg text-primary mb-2 tracking-wider;
}

.benefits-title {
  @apply text-3xl md:text-4xl font-bold mb-4;
}

.benefits-description {
  @apply text-xl text-muted-foreground mb-8;
}

.benefits-grid {
  @apply grid lg:grid-cols-2 gap-4 w-full;
}

.benefit-card {
  @apply bg-muted/50 dark:bg-card hover:bg-background dark:hover:bg-background transition-all delay-75;
}

.benefit-header {
  @apply flex justify-between;
}

.benefit-icon {
  @apply size-8 mb-6 text-primary;
}

.benefit-number {
  @apply text-5xl text-muted-foreground/15 font-medium transition-all delay-75 group-hover/number:text-muted-foreground/30;
}

.benefit-description {
  @apply text-muted-foreground;
}
</style>
